import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import SimpleLayout from '../components/SimpleLayout';
import Footer from '../components/Footer';
import cookieLogo from '../assets/cookie.png';

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInStagger = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;



// Styled components
const PageBackground = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const HeroSection = styled.div`
  width: 100%;
  color: #fff;
  background: transparent;
  padding: 2rem 2rem 1rem 2rem;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 3rem;
  max-width: 1400px;
  margin: 0 auto;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem 1rem 1rem;
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
`;

const LogoContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Logo = styled.img`
  width: 180px;
  animation: ${float} 8s ease-in-out infinite;
  filter: drop-shadow(0 10px 20px rgba(45, 108, 255, 0.3));
  
  @media (max-width: 768px) {
    width: 140px;
  }
`;



const DocMessage = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-align: center;
  padding: 0.75rem;
  font-weight: 600;
  font-size: 1.1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
`;

const HeroContent = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 2.8rem;
  margin-bottom: 1rem;
  color: transparent;
  background: linear-gradient(135deg, #6ea8ff 0%, #ffffff 50%, #3a8fff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  font-weight: 900;
  letter-spacing: -0.02em;
  text-shadow: none;
  animation: ${fadeIn} 1s ease-out;
  position: relative;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #b6d0ff;
  margin-bottom: 0;
  max-width: 600px;
  line-height: 1.5;
  animation: ${fadeIn} 1.2s ease-out 0.3s both;
  font-weight: 400;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.4;
    margin-left: auto;
    margin-right: auto;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const ToolsSection = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem 2rem 2rem;
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 1.5rem 1rem;
  }
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const ToolCard = styled(Link)`
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  padding: 2rem 2rem;
  text-decoration: none;
  color: #fff;
  border: 1px solid rgba(110, 168, 255, 0.2);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: block;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  animation: ${fadeInStagger} 0.8s ease-out;
  animation-delay: ${props => props.$index * 0.1}s;
  animation-fill-mode: both;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      transparent, 
      rgba(110, 168, 255, 0.1), 
      transparent
    );
    transition: left 0.6s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(145deg, rgba(110, 168, 255, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 24px;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(110, 168, 255, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    border-color: rgba(110, 168, 255, 0.5);
    text-decoration: none;
    color: #fff;
    
    &::before {
      left: 100%;
    }
    
    &::after {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(-4px) scale(0.98);
    transition: transform 0.1s ease;
  }
  
  ${props => props.$disabled && `
    opacity: 0.7;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }
    &:active {
      transform: none;
    }
  `}
`;



const ToolTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const ToolDescription = styled.p`
  font-size: 1.1rem;
  color: #b6d0ff;
  line-height: 1.6;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const ToolStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
  
  background: ${props => 
    props.status === 'available' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
    props.status === 'beta' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
    'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
  };
  
  color: #fff;
  box-shadow: 0 4px 12px ${props => 
    props.status === 'available' ? 'rgba(16, 185, 129, 0.3)' :
    props.status === 'beta' ? 'rgba(245, 158, 11, 0.3)' :
    'rgba(107, 114, 128, 0.3)'
  };
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    animation: ${shimmer} 2s infinite;
    animation-delay: ${props => props.status === 'available' ? '0s' : '1s'};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.8rem;
`;

const FeatureItem = styled.li`
  color: #b6d0ff;
  font-size: 0.95rem;
  padding-left: 1.5rem;
  position: relative;
  opacity: 0.9;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #10b981;
    font-weight: bold;
    text-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
  }
`;

const ComingSoonOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  
  &::before {
    content: 'Check Back Soon';
    color: #fff;
    font-size: 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    margin-bottom: 0.5rem;
  }
  
  &::after {
    content: 'New tools in development';
    color: #b6d0ff;
    font-size: 0.9rem;
    font-weight: 400;
    opacity: 0.8;
  }
`;

const BlurredContent = styled.div`
  filter: blur(3px);
  opacity: 0.3;
  pointer-events: none;
  user-select: none;
`;

const CallToActionButton = styled.button`
  background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.8rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(110, 168, 255, 0.3);
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(110, 168, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PricingBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
`;

const StatsSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: #6ea8ff;
  text-shadow: 0 0 20px rgba(110, 168, 255, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #b6d0ff;
  opacity: 0.8;
  margin-top: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TestimonialSection = styled.div`
  max-width: 1400px;
  margin: 3rem auto 0 auto;
  text-align: center;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin: 2rem auto 0 auto;
    padding: 0 1rem;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.15);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const TestimonialText = styled.p`
  font-size: 0.95rem;
  color: #e1e9ff;
  line-height: 1.5;
  font-style: italic;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TestimonialAuthor = styled.div`
  color: #6ea8ff;
  font-weight: 600;
  font-size: 0.85rem;
`;

const TestimonialHeader = styled.h3`
  font-size: 1.5rem;
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 700;
`;

function GatewayHomePage() {
  const navigate = useNavigate();

  const tools = [
    {
      title: 'DeservedWarmer',
      description: 'Automate browser behavior to build trusted accounts on Amazon, eBay, Vinted, and more. Create human-like browsing patterns to prevent suspensions.',
      link: '/warmer',
      status: 'available',
      pricing: 'From $60/mo',
      cta: 'Start Warming',
      features: [
        'Local automation tool',
        'Proxy support',
        'Multiple platform support',
        'Human-like behavior simulation',
        'Privacy-first approach'
      ]
    },
    {
      title: 'DeservedCam',
              description: 'Professional virtual webcam solution for identity verification. Stream ID photos with realistic camera effects and natural appearance.',
      link: '/camera',
      status: 'available',
      pricing: 'One-time $300',
      cta: 'Get Camera Tool',
      features: [
        'Virtual webcam driver',
                  'ID photo camera effects',
                  'Authentic camera simulation',
        'Works with verification sites',
        'Lifetime license available'
      ]
    },
    {
      title: 'More Tools',
      description: 'Additional automation and productivity tools are in development. Revolutionary features coming soon.',
      link: '/coming-soon',
      status: 'coming-soon',
      pricing: 'TBA',
      cta: 'Notify Me',
      features: [
        'Social media automation',
        'Email management suite',
        'Profile generation tools',
        'Multi-platform sync',
        'Advanced AI scheduling'
      ]
    }
  ];

  return (
    <PageBackground>
      <BackgroundPattern />
      <SimpleLayout>
        <MainContent>
          <HeroSection>
            <LogoContainer>
              <Logo src={cookieLogo} alt="Deserved Logo" />
            </LogoContainer>
            <HeroContent>
              <Title>DESERVED TOOLS</Title>
              <Subtitle>
                Save time and increase success rates with our automation tools. 
                Build trusted accounts, pass verifications, and automate repetitive tasks - all running locally on your machine.
              </Subtitle>
              <StatsSection>
                <StatItem>
                  <StatNumber>300+</StatNumber>
                  <StatLabel>Active Users</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>100%</StatNumber>
                  <StatLabel>Success Rate</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>24/7</StatNumber>
                  <StatLabel>Support</StatLabel>
                </StatItem>
              </StatsSection>
            </HeroContent>
          </HeroSection>

          <ToolsSection>
            <ToolsGrid>
              {tools.map((tool, index) => (
                <ToolCard 
                  key={index} 
                  to={tool.status === 'coming-soon' ? '#' : tool.link}
                  onClick={tool.status === 'coming-soon' ? (e) => e.preventDefault() : undefined}
                  $disabled={tool.status === 'coming-soon'}
                  $index={index}
                >
                  {tool.status !== 'coming-soon' && (
                    <PricingBadge>{tool.pricing}</PricingBadge>
                  )}
                  
                  {tool.status === 'coming-soon' ? (
                    <BlurredContent>
                      <ToolTitle>{tool.title}</ToolTitle>
                      <ToolStatus status={tool.status}>Coming Soon</ToolStatus>
                      <ToolDescription>{tool.description}</ToolDescription>
                      <FeaturesList>
                        {tool.features.map((feature, idx) => (
                          <FeatureItem key={idx}>{feature}</FeatureItem>
                        ))}
                      </FeaturesList>
                    </BlurredContent>
                  ) : (
                    <>
                      <ToolTitle>{tool.title}</ToolTitle>
                      <ToolStatus status={tool.status}>
                        {tool.status === 'available' ? 'Available Now' : 'Beta'}
                      </ToolStatus>
                      <ToolDescription>{tool.description}</ToolDescription>
                      <FeaturesList>
                        {tool.features.map((feature, idx) => (
                          <FeatureItem key={idx}>{feature}</FeatureItem>
                        ))}
                      </FeaturesList>
                      <CallToActionButton>
                        {tool.cta} →
                      </CallToActionButton>
                    </>
                  )}
                  {tool.status === 'coming-soon' && <ComingSoonOverlay />}
                </ToolCard>
              ))}
            </ToolsGrid>
            
            <TestimonialSection>
              <TestimonialHeader>What Our Users Say</TestimonialHeader>
              <TestimonialGrid>
                <TestimonialCard>
                  <TestimonialText>
                    "Gone from doing 1 account every 2-3 weeks to doing 4-5 accounts every week. The warming patterns are so realistic, I haven't had a single suspension in 3 months."
                  </TestimonialText>
                  <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                </TestimonialCard>
                
                <TestimonialCard>
                  <TestimonialText>
                    "Was skeptical at first but DeservedCam saved me from manual verification hell. Passed 12 verifications in a row without any issues. Worth every penny."
                  </TestimonialText>
                  <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                </TestimonialCard>
                
                <TestimonialCard>
                  <TestimonialText>
                    "My Amazon accounts used to get flagged within days. Now they run for months without problems. The automation looks completely natural."
                  </TestimonialText>
                  <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                </TestimonialCard>
              </TestimonialGrid>
            </TestimonialSection>
          </ToolsSection>
        </MainContent>
        
        <Footer />
      </SimpleLayout>
    </PageBackground>
  );
}

export default GatewayHomePage; 